import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

// Context imports
import { AuthProvider } from './context/AuthContext';

// Component imports
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import WhatsAppButton from './components/WhatsAppButton';
import GlobalCartButton from './components/GlobalCartButton';

// Page imports
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import Auth from './pages/Auth';
import Profile from './pages/Profile';
import PlaceOrder from './pages/PlaceOrder';
import SelectItems from './pages/SelectItems';
import AdminDashboard from './pages/AdminDashboard';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.main`
  flex: 1;
`;

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time for the iron animation
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <AppContainer>
          <AnimatePresence mode="wait">
            {loading ? (
              <LoadingScreen key="loading" />
            ) : (
              <motion.div
                key="app"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <WhatsAppButton phoneNumber="919755504400" />
                <GlobalCartButton />
                <Routes>
                  <Route path="/login" element={
                    <>
                      <Navbar />
                      <ContentContainer>
                        <Auth />
                      </ContentContainer>
                      <Footer />
                    </>
                  } />
                  <Route path="/profile" element={
                    <>
                      <Navbar />
                      <ProtectedRoute>
                        <ContentContainer>
                          <Profile />
                        </ContentContainer>
                      </ProtectedRoute>
                      <Footer />
                    </>
                  } />
                  <Route path="/select-items" element={
                    <>
                      <Navbar />
                      <ContentContainer>
                        <SelectItems />
                      </ContentContainer>
                      <Footer />
                    </>
                  } />
                  <Route path="/place-order" element={
                    <>
                      <Navbar />
                      <ProtectedRoute>
                        <ContentContainer>
                          <PlaceOrder />
                        </ContentContainer>
                      </ProtectedRoute>
                      <Footer />
                    </>
                  } />
                  <Route path="/admin/dashboard" element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/" element={
                    <>
                      <Navbar />
                      <ContentContainer>
                        <Home />
                      </ContentContainer>
                      <Footer />
                    </>
                  } />
                  <Route path="/services" element={
                    <>
                      <Navbar />
                      <ContentContainer>
                        <Services />
                      </ContentContainer>
                      <Footer />
                    </>
                  } />
                  <Route path="/about" element={
                    <>
                      <Navbar />
                      <ContentContainer>
                        <About />
                      </ContentContainer>
                      <Footer />
                    </>
                  } />
                  <Route path="/pricing" element={
                    <>
                      <Navbar />
                      <ContentContainer>
                        <Pricing />
                      </ContentContainer>
                      <Footer />
                    </>
                  } />
                  <Route path="/contact" element={
                    <>
                      <Navbar />
                      <ContentContainer>
                        <Contact />
                      </ContentContainer>
                      <Footer />
                    </>
                  } />
                </Routes>
              </motion.div>
            )}
          </AnimatePresence>
        </AppContainer>
      </AuthProvider>
    </Router>
  );
}

export default App;
